/**
 * `/projects/new` (PRD §4.4) — plain master data, G-Admin only. The company can be pre-selected
 * through `?company=…` so the button on a company page lands here with the tenant already set.
 */

import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BackLink } from "@/components/back-link";
import { ProjectForm } from "@/components/project-form";
import { describeError } from "@/lib/errors";
import { useCreateProject } from "@/lib/tenancy";

export function ProjectNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const create = useCreateProject();

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink to="/projects">{t("projectsList.title")}</BackLink>

      <h1 className="mt-4 text-4xl">{t("projectForm.title")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("projectForm.subtitle")}</p>

      <div className="mt-8 rounded-xl border border-border bg-card p-6 shadow-soft">
        <ProjectForm
          initial={{ companyId: params.get("company") ?? "" }}
          submitLabel={t("projectForm.submit")}
          busy={create.isPending}
          onCancel={() => navigate("/projects")}
          onSubmit={(values) =>
            create.mutate(
              {
                ...(values.newCompany
                  ? { newCompany: values.newCompany }
                  : { companyId: values.companyId }),
                name: values.name,
                startDate: values.startDate,
                endDate: values.endDate,
                bsfzProjectDescription: values.bsfzProjectDescription || null,
                projectCode: values.projectCode || null,
              },
              {
                onSuccess: (data) => {
                  toast.success(t("projectForm.created"));
                  navigate(`/projects/${data.project.id}/live`);
                },
                onError: (error) => toast.error(describeError(error)),
              },
            )
          }
        />
      </div>
    </div>
  );
}
